import * as React from 'react';

import Modal from 'components/Modal/Modal';

import { useBnplPromo } from 'hooks/bnpl/useBnplPromo';

import { TService, TServiceOption } from 'types/service';

import {
  getBnplUnitPrice,
  getDepositText,
  getDisplayedPrice,
  getDurationText,
  getOptionText,
} from 'utils/serviceSelectionUtils';

import { TServiceProps } from './useServicesPage';

type UseServiceSelectionParams = {
  disabled: boolean;
  service: TService;
  selectedOption?: TServiceOption;
  onClick: TServiceProps['onClick'];
};

export const useServiceSelection = ({
  service,
  selectedOption,
  disabled,
  onClick,
}: UseServiceSelectionParams) => {
  const { guid, options } = service;
  const hasOptions = Boolean(options?.length);
  const optionOrOptions = hasOptions
    ? selectedOption || service.options
    : undefined;
  const serviceOrOptionOrOptions = hasOptions
    ? selectedOption || (options as TServiceOption[])
    : service;

  const bnplPromo = useBnplPromo();
  const bnplUnitPrice = bnplPromo.isEnabled
    ? getBnplUnitPrice(serviceOrOptionOrOptions)
    : undefined;

  const [showMobileDescription, setShowMobileDescription] =
    React.useState(false);

  const [showNoMatchingProviderModal, setShowNoMatchingProviderModal] =
    React.useState(false);

  const onServiceClick = () => {
    if (!disabled) {
      return onClick(guid);
    }
  };

  const toggleNoMatchingProvidersModal = () => {
    setShowNoMatchingProviderModal(!showNoMatchingProviderModal);
  };

  const toggleMobileDescription = () => {
    setShowMobileDescription(!showMobileDescription);
  };

  const displayedPrice = React.useMemo(
    () => getDisplayedPrice(serviceOrOptionOrOptions),
    [serviceOrOptionOrOptions],
  );

  const durationText = getDurationText(serviceOrOptionOrOptions);

  const depositText = getDepositText(serviceOrOptionOrOptions);

  const optionText = getOptionText(optionOrOptions);

  const renderNoMatchingProviderModal = () => {
    return showNoMatchingProviderModal ? (
      <Modal
        title="No Matching Provider"
        onClose={toggleNoMatchingProvidersModal}
      >
        The service combo you have selected does not have any matching
        providers. Please book separate appointments if you would like to access
        these services.
      </Modal>
    ) : null;
  };

  return {
    bnplUnitPrice,
    bnplCapabilities: bnplPromo.bnplCapabilities,
    displayedPrice,
    toggleMobileDescription,
    showMobileDescription,
    toggleNoMatchingProvidersModal,
    showNoMatchingProviderModal,
    onServiceClick,
    depositText,
    durationText,
    optionText,
    renderNoMatchingProviderModal,
  };
};
